/**
 * PublicSoulForm — the branded, token-authenticated member-discovery
 * experience served at /soul/:token. NO LOGIN. Mobile-first. Dark navy
 * (#1C304A) background with cream (#EEECE1) text. One question per
 * screen. Answers auto-save on every "Next" tap.
 *
 * Per Dipali's spec (2026-02):
 *   "Elegant, luxury feel — NOT a form, an EXPERIENCE."
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const NAVY = '#1C304A';
const CREAM = '#EEECE1';
const GOLD = '#C9A66B';
const SOFT_BORDER = 'rgba(238, 236, 225, 0.18)';
const SOFT_FILL = 'rgba(238, 236, 225, 0.05)';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicSoulForm() {
  const { token } = useParams();
  const [stage, setStage] = useState('loading'); // loading | error | cover | q | done | already
  const [errorMsg, setErrorMsg] = useState('');
  const [ctx, setCtx] = useState(null);    // { first_name, lsm_name, answers, ... }
  const [fields, setFields] = useState([]);
  const [folders, setFolders] = useState([]);  // 8-folder vocabulary
  const [idx, setIdx] = useState(0);       // current question index (0..fields.length-1)
  const [draft, setDraft] = useState({});  // local working copy of all answers
  const [saving, setSaving] = useState(false);

  // ── Load schema + member context in parallel.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [schemaRes, ctxRes] = await Promise.all([
          axios.get(`${API}/soul-form/schema`),
          axios.get(`${API}/soul-form/${token}`),
        ]);
        if (!alive) return;
        setFields(schemaRes.data.fields || []);
        setFolders(schemaRes.data.folders || []);
        setCtx(ctxRes.data);
        setDraft(ctxRes.data.answers || {});
        if (ctxRes.data.already_completed) {
          setStage('already');
        } else {
          setStage('cover');
        }
      } catch (e) {
        if (!alive) return;
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 410) {
          setErrorMsg(detail || 'This Soul Form link has expired.');
        } else if (status === 404) {
          setErrorMsg(detail || 'This Soul Form link is invalid.');
        } else {
          setErrorMsg('Could not open the form. Please try again or contact your concierge.');
        }
        setStage('error');
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const total = fields.length;
  const current = fields[idx];
  const answeredCount = useMemo(() => fields.filter(
    f => {
      if (f.type === 'group') {
        return (f.fields || []).some(sub => {
          const v = draft[sub.key];
          return v !== undefined && v !== null && v !== '';
        });
      }
      const v = draft[f.key];
      if (v === undefined || v === null || v === '') return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    }
  ).length, [draft, fields]);

  // Conditional skip — when a member skips on a "family" screen
  // (children_info, spouse_group, child_N_group), they're saying "I'm
  // done with that section." So we jump past every remaining child
  // screen and land on the next non-family field. No more clicking
  // Skip 4 times through empty child forms.
  const FAMILY_SKIP_KEYS = new Set([
    'children_info',
    'spouse_group',
    'child_1_group', 'child_2_group',
    'child_3_group', 'child_4_group',
  ]);
  const isFamilyKey = (key) => key && key.startsWith('child_') && key.endsWith('_group')
    || key === 'children_info' || key === 'spouse_group';
  const findNextNonFamily = (fromIdx) => {
    for (let i = fromIdx; i < fields.length; i += 1) {
      if (!isFamilyKey(fields[i]?.key)) return i;
    }
    return fields.length;  // off-the-end → finish
  };

  // ── Save the current answer, then advance.
  const handleNext = async (skip = false) => {
    if (!current) return;
    setSaving(true);
    try {
      if (current.type === 'group') {
        // Save every sub-field (or null them all on skip). We chain PATCHes
        // so the server's progress counter increments correctly, but the
        // round-trip is tiny (<80ms each) so the UX stays fluid.
        for (const sub of (current.fields || [])) {
          const v = skip ? null : draft[sub.key];
          await axios.patch(`${API}/soul-form/${token}`, {
            key: sub.key,
            value: v === undefined ? null : v,
          });
        }
      } else {
        const value = skip ? null : draft[current.key];
        await axios.patch(`${API}/soul-form/${token}`, {
          key: current.key,
          value: value === undefined ? null : value,
        });
      }

      // Decide where to jump next.
      let nextIdx = idx + 1;

      // Smart skip: if the user clicked Skip on a family screen, fast-
      // forward past every other family screen in one go.
      if (skip && FAMILY_SKIP_KEYS.has(current.key)) {
        nextIdx = findNextNonFamily(idx + 1);
        // Mark every fast-forwarded family screen as null on the server too
        // so completion progress + downstream Soul Profile generation isn't
        // confused by half-saved drafts.
        const fastForwarded = fields.slice(idx + 1, nextIdx);
        await Promise.all(fastForwarded.flatMap(f => {
          if (f.type === 'group') {
            return (f.fields || []).map(sub => axios.patch(
              `${API}/soul-form/${token}`,
              { key: sub.key, value: null },
            ).catch(() => null));
          }
          return [axios.patch(
            `${API}/soul-form/${token}`,
            { key: f.key, value: null },
          ).catch(() => null)];
        }));
      }

      if (nextIdx >= total) {
        await axios.post(`${API}/soul-form/${token}/complete`);
        setStage('done');
      } else {
        setIdx(nextIdx);
      }
    } catch {
      if (idx >= total - 1) setStage('done');
      else setIdx(idx + 1);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (idx > 0) setIdx(idx - 1);
  };

  // ── Renderers
  if (stage === 'loading') {
    return (
      <Shell>
        <div className="text-center" style={{ color: CREAM }}>
          <Logo />
          <div className="mt-8 text-sm opacity-70" data-testid="soul-form-loading">Opening your Soul Form…</div>
        </div>
      </Shell>
    );
  }

  if (stage === 'error') {
    return (
      <Shell>
        <div className="text-center max-w-md" data-testid="soul-form-error">
          <Logo />
          <h1 className="mt-10 text-3xl font-serif" style={{ color: CREAM }}>Link unavailable</h1>
          <p className="mt-4 text-sm leading-relaxed" style={{ color: CREAM, opacity: 0.7 }}>{errorMsg}</p>
          <p className="mt-6 text-xs" style={{ color: GOLD }}>
            Please reach out to your concierge for a fresh link.
          </p>
        </div>
      </Shell>
    );
  }

  if (stage === 'already') {
    return (
      <Shell>
        <div className="text-center max-w-md" data-testid="soul-form-already-completed">
          <Logo />
          <h1 className="mt-10 text-3xl font-serif" style={{ color: CREAM }}>Thank you, {ctx?.first_name}.</h1>
          <p className="mt-4 text-sm leading-relaxed" style={{ color: CREAM, opacity: 0.78 }}>
            Your Soul Form is already on file. Our concierge team uses your answers
            to curate every recommendation we send your way.
          </p>
          <p className="mt-6 text-xs italic" style={{ color: GOLD, opacity: 0.85 }}>
            If anything has changed and you'd like to update your preferences,
            please write to your concierge — they'll send a fresh link.
          </p>
        </div>
      </Shell>
    );
  }

  if (stage === 'done') {
    return (
      <Shell>
        <div className="text-center max-w-md" data-testid="soul-form-done">
          <Logo />
          <div className="mt-12 text-6xl font-serif" style={{ color: GOLD }}>·</div>
          <h1 className="mt-4 text-3xl font-serif" style={{ color: CREAM }}>
            Thank you, {ctx?.first_name}.
          </h1>
          <p className="mt-5 text-sm leading-relaxed" style={{ color: CREAM, opacity: 0.78 }}>
            Your Soul has been carefully read.
          </p>
          <p className="mt-3 text-sm leading-relaxed" style={{ color: CREAM, opacity: 0.7 }}>
            From here on, every recommendation we send — every dining table,
            every escape, every quiet surprise — will be shaped by what you've
            shared with us.
          </p>
          <p className="mt-8 text-[11px] tracking-[0.2em] uppercase" style={{ color: GOLD }}>
            {ctx?.lsm_name || 'Your Concierge'} · Les Concierges Group
          </p>
        </div>
      </Shell>
    );
  }

  // stage === 'cover'
  if (stage === 'cover') {
    return (
      <Shell>
        <div className="text-center max-w-md" data-testid="soul-form-cover">
          <Logo />
          <p className="mt-12 text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD }}>
            A few minutes about what you love
          </p>
          <h1 className="mt-5 text-4xl sm:text-5xl font-serif leading-tight" style={{ color: CREAM }}>
            Welcome,<br/>{ctx?.first_name}.
          </h1>
          <p className="mt-7 text-sm leading-relaxed" style={{ color: CREAM, opacity: 0.78 }}>
            We'd like to know you a little better — your favourite cuisines,
            the journeys still on your bucket list, the celebrations that matter.
          </p>
          <p className="mt-4 text-sm leading-relaxed" style={{ color: CREAM, opacity: 0.7 }}>
            A few short questions. Under five minutes.
            <br/>Every answer shapes how your concierge cares for you.
          </p>
          <button
            onClick={() => setStage('q')}
            data-testid="soul-form-begin-btn"
            className="mt-12 px-10 py-3.5 text-[12px] tracking-[0.22em] uppercase transition-all hover:opacity-90"
            style={{ background: GOLD, color: NAVY, fontWeight: 600,
                     borderRadius: 2, letterSpacing: '0.22em' }}>
            Begin
          </button>
          <p className="mt-8 text-[10px] tracking-[0.2em] uppercase" style={{ color: CREAM, opacity: 0.45 }}>
            Concierge<sup style={{ fontSize: 7, top: -3 }}>®</sup> · Les Concierges Group
          </p>
        </div>
      </Shell>
    );
  }

  // stage === 'q'
  return (
    <Shell>
      {/* Progress bar — fixed top */}
      <div className="fixed top-0 left-0 right-0 z-10" style={{ background: NAVY }}>
        <div className="max-w-xl mx-auto px-5 pt-5 pb-3">
          <div className="flex items-center justify-between text-[10px] tracking-[0.18em] uppercase mb-2"
               style={{ color: CREAM, opacity: 0.65 }}>
            <span data-testid="soul-form-progress-label">{idx + 1} of {total}</span>
            <span style={{ color: GOLD }}>Concierge<sup style={{ fontSize: 6, top: -3 }}>®</sup></span>
          </div>
          <div className="h-[2px] w-full" style={{ background: 'rgba(238,236,225,0.15)' }}>
            <div className="h-full transition-all duration-500 ease-out"
                 data-testid="soul-form-progress-bar"
                 style={{ width: `${((idx + 1) / total) * 100}%`, background: GOLD }} />
          </div>
        </div>
      </div>

      <div className="w-full max-w-xl mx-auto pt-24 pb-32 px-5" data-testid={`soul-form-q-${current.key}`}>
        {/* Section header — renders only on the first question of a new folder.
            Gives the member a coherent reading of why this block exists. */}
        {(() => {
          if (!current.folder) return null;
          const prev = idx > 0 ? fields[idx - 1] : null;
          const isFolderOpener = !prev || prev.folder !== current.folder;
          if (!isFolderOpener) return null;
          const folder = folders.find(f => f.id === current.folder);
          if (!folder) return null;
          return (
            <div className="mb-8" data-testid={`soul-form-folder-${folder.id}`}>
              <p className="text-[9px] tracking-[0.32em] uppercase"
                 style={{ color: GOLD, fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
                {folder.id === 'meta' ? '— · —' : `Folder · ${folder.id}`}
              </p>
              <h3 className="text-[15px] tracking-[0.08em] uppercase mt-1.5"
                  style={{ color: CREAM, fontFamily: 'Inter, sans-serif', fontWeight: 500, opacity: 0.85 }}>
                {folder.name}
              </h3>
              {folder.intro && (
                <p className="mt-2 text-[13px] italic"
                   style={{ color: CREAM, opacity: 0.55 }}>
                  {folder.intro}
                </p>
              )}
              <div className="mt-5 h-px w-12" style={{ background: GOLD, opacity: 0.6 }} />
            </div>
          );
        })()}
        {current.is_golden && (
          <p className="text-[10px] tracking-[0.32em] uppercase mb-4" style={{ color: GOLD }}>
            One last thing.
          </p>
        )}
        <h2 className="text-[26px] sm:text-[30px] font-serif leading-tight"
            style={{ color: CREAM }}>
          {current.label}
        </h2>
        {current.placeholder && (
          <p className="mt-3 text-[12px] italic" style={{ color: CREAM, opacity: 0.55 }}>
            {current.placeholder.startsWith('Tell us') ? current.placeholder :
              `e.g. ${current.placeholder.replace(/^e\.g\.\s*/, '')}`}
          </p>
        )}

        <div className="mt-9">
          <QuestionField
            field={current}
            value={draft[current.key]}
            onChange={(val) => setDraft({ ...draft, [current.key]: val })}
            draft={draft}
            setDraft={setDraft}
          />
        </div>
      </div>

      {/* Footer — fixed bottom action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-10"
           style={{ background: NAVY,
                    boxShadow: '0 -10px 24px -10px rgba(0,0,0,0.5)' }}>
        <div className="max-w-xl mx-auto px-5 py-4 flex items-center gap-3">
          <button
            onClick={handleBack}
            disabled={idx === 0 || saving}
            data-testid="soul-form-back-btn"
            className="text-[11px] tracking-[0.18em] uppercase transition-opacity disabled:opacity-30"
            style={{ color: CREAM, opacity: 0.7, padding: '10px 14px' }}>
            ← Back
          </button>
          {current.skippable && (
            <button
              onClick={() => handleNext(true)}
              disabled={saving}
              data-testid="soul-form-skip-btn"
              className="text-[11px] tracking-[0.18em] uppercase ml-auto transition-opacity hover:opacity-90"
              style={{ color: CREAM, opacity: 0.55, padding: '10px 14px' }}>
              Skip
            </button>
          )}
          <button
            onClick={() => handleNext(false)}
            disabled={saving}
            data-testid="soul-form-next-btn"
            className={`px-8 py-3 text-[11px] tracking-[0.22em] uppercase transition-all hover:opacity-90 disabled:opacity-50 ${current.skippable ? '' : 'ml-auto'}`}
            style={{ background: GOLD, color: NAVY, fontWeight: 600, borderRadius: 2 }}>
            {saving ? 'Saving…' : (idx >= total - 1 ? 'Done' : 'Next →')}
          </button>
        </div>
        <div className="text-center pb-2 text-[9px] tracking-[0.2em]"
             style={{ color: CREAM, opacity: 0.35 }}>
          {answeredCount} answered · auto-saved
        </div>
      </div>
    </Shell>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────────
function Shell({ children }) {
  return (
    <div className="min-h-screen w-full flex items-start justify-center"
         style={{
           background: NAVY,
           fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
           paddingTop: 'env(safe-area-inset-top)',
           paddingBottom: 'env(safe-area-inset-bottom)',
         }}>
      <style>{`
        body { background: ${NAVY}; }
        ::selection { background: ${GOLD}; color: ${NAVY}; }
        input, textarea {
          font-family: "Cormorant Garamond", "Playfair Display", Georgia, serif;
        }
        input::placeholder, textarea::placeholder {
          color: rgba(238, 236, 225, 0.35);
          font-style: italic;
        }
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=Inter:wght@400;500;600&display=swap');
      `}</style>
      <div className="w-full min-h-screen flex flex-col items-center justify-center px-5 py-16">
        {children}
      </div>
    </div>
  );
}

function Logo() {
  return (
    <div className="text-center">
      <div className="text-[11px] tracking-[0.42em] uppercase"
           style={{ color: GOLD, fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
        Concierge<sup style={{ fontSize: 7, top: -4 }}>®</sup>
      </div>
      <div className="text-[9px] tracking-[0.24em] uppercase mt-1.5"
           style={{ color: CREAM, opacity: 0.45, fontFamily: 'Inter, sans-serif' }}>
        Les Concierges Group
      </div>
    </div>
  );
}

function QuestionField({ field, value, onChange, draft, setDraft }) {
  const inputStyle = {
    background: SOFT_FILL,
    color: CREAM,
    border: `1px solid ${SOFT_BORDER}`,
    borderRadius: 4,
    fontSize: 18,
    padding: '14px 18px',
    width: '100%',
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: '"Cormorant Garamond", Georgia, serif',
  };

  // Group — renders every sub-field vertically with its own label.
  if (field.type === 'group') {
    return (
      <div className="space-y-4" data-testid={`soul-form-group-${field.key}`}>
        {(field.fields || []).map((sub) => (
          <div key={sub.key}>
            <label className="text-[11px] tracking-[0.16em] uppercase block mb-2"
                   style={{ color: CREAM, opacity: 0.55, fontFamily: 'Inter, sans-serif' }}>
              {sub.label}
            </label>
            <input
              type={sub.type === 'date' ? 'date' : (sub.type === 'email' ? 'email' : 'text')}
              value={draft?.[sub.key] || ''}
              placeholder={sub.placeholder || ''}
              onChange={(e) => setDraft({ ...draft, [sub.key]: e.target.value })}
              data-testid={`soul-form-input-${sub.key}`}
              style={{ ...inputStyle, fontSize: 16, padding: '12px 16px' }}
              onFocus={(e) => e.target.style.borderColor = GOLD}
              onBlur={(e) => e.target.style.borderColor = SOFT_BORDER}
            />
          </div>
        ))}
      </div>
    );
  }

  if (field.type === 'text') {
    return (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        data-testid={`soul-form-input-${field.key}`}
        style={{ ...inputStyle, resize: 'vertical', minHeight: 90 }}
        onFocus={(e) => e.target.style.borderColor = GOLD}
        onBlur={(e) => e.target.style.borderColor = SOFT_BORDER}
      />
    );
  }

  if (field.type === 'radio') {
    const arr = Array.isArray(value) ? value[0] : value;
    return (
      <div className="space-y-2.5" data-testid={`soul-form-radio-${field.key}`}>
        {field.options.map(opt => (
          <OptionPill
            key={opt}
            label={opt}
            selected={arr === opt}
            onClick={() => onChange(opt)}
            testId={`soul-form-radio-${field.key}-${opt.replace(/\s+/g, '-').toLowerCase()}`}
          />
        ))}
      </div>
    );
  }

  if (field.type === 'checkboxes') {
    const arr = Array.isArray(value) ? value : (value ? [value] : []);
    const toggle = (opt) => {
      if (arr.includes(opt)) onChange(arr.filter(x => x !== opt));
      else onChange([...arr, opt]);
    };
    return (
      <div className="space-y-2.5" data-testid={`soul-form-cb-${field.key}`}>
        {field.options.map(opt => (
          <OptionPill
            key={opt}
            label={opt}
            selected={arr.includes(opt)}
            onClick={() => toggle(opt)}
            testId={`soul-form-cb-${field.key}-${opt.replace(/\s+/g, '-').toLowerCase()}`}
          />
        ))}
      </div>
    );
  }

  if (field.type === 'checkboxes_with_other') {
    // We store this as { selected: [...], other: "..." } so the soul_form
    // reflects the full intent. soul_engine flattens lists to comma-joined.
    const obj = (value && typeof value === 'object' && !Array.isArray(value))
      ? value : (Array.isArray(value) ? { selected: value, other: '' } : { selected: [], other: '' });
    const selected = obj.selected || [];
    const other = obj.other || '';
    const toggle = (opt) => {
      const next = selected.includes(opt)
        ? selected.filter(x => x !== opt)
        : [...selected, opt];
      onChange({ selected: next, other });
    };
    return (
      <div className="space-y-2.5" data-testid={`soul-form-cbo-${field.key}`}>
        {field.options.map(opt => (
          <OptionPill
            key={opt}
            label={opt}
            selected={selected.includes(opt)}
            onClick={() => toggle(opt)}
            testId={`soul-form-cbo-${field.key}-${opt.replace(/\s+/g, '-').toLowerCase()}`}
          />
        ))}
        <input
          type="text"
          placeholder="Other (please specify)"
          value={other}
          onChange={(e) => onChange({ selected, other: e.target.value })}
          data-testid={`soul-form-cbo-${field.key}-other`}
          style={{ ...inputStyle, fontSize: 16, padding: '12px 16px', marginTop: 6 }}
          onFocus={(e) => e.target.style.borderColor = GOLD}
          onBlur={(e) => e.target.style.borderColor = SOFT_BORDER}
        />
      </div>
    );
  }

  return null;
}

function OptionPill({ label, selected, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="w-full text-left transition-all"
      style={{
        background: selected ? 'rgba(201, 166, 107, 0.18)' : SOFT_FILL,
        border: `1px solid ${selected ? GOLD : SOFT_BORDER}`,
        color: CREAM,
        borderRadius: 4,
        padding: '13px 18px',
        fontSize: 16,
        fontFamily: '"Cormorant Garamond", Georgia, serif',
        cursor: 'pointer',
      }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
        <span style={{
          width: 14, height: 14, borderRadius: 1,
          border: `1px solid ${selected ? GOLD : 'rgba(238,236,225,0.4)'}`,
          background: selected ? GOLD : 'transparent',
          display: 'inline-block', flexShrink: 0,
        }} />
        {label}
      </span>
    </button>
  );
}
